// Upsell touchpoints (#350) — which tier actually unlocks the thing the user
// just hit, and the copy that names it.
//
// Two hard rules live here so the three call sites can't each get them wrong:
//
//  1. NEVER hardcode a tier name. "Upgrade to Solo" is only true while Solo is
//     the cheapest tier that unlocks watermark removal. The unlock tier is
//     DERIVED from the catalog (pricing/plans.js) every time, so a catalog
//     change moves the copy with it instead of leaving a lie in three files.
//  2. NEVER pitch an upgrade that doesn't exist or that would shrink the user.
//     resolveUnlockTier returns null when no self-serve tier improves on what
//     they already have (Pro is the top tier; a grandfathered floor can exceed
//     the catalog) — callers must then withhold the upgrade path and offer only
//     the self-service remedy.
//
// Feature names come from lib/operationCopy.js (#379) so the vocabulary matches
// the control the user was just looking at.

import { PRICING_TIERS } from '../pricing/plans';
import { poolBytesFromGb } from '../photoUpload/caps';
import { OPERATIONS } from '../lib/operationCopy';

// Stable ids — these ride on every funnel event as `prompt_id`, so they are the
// GA4 dimension values that answer "which touchpoint converts". Renaming one
// forks the reporting; add instead.
export const UPSELL_PROMPTS = {
  WATERMARK_LOCKED: 'watermark_locked',
  STORAGE_POOL_FULL: 'storage_pool_full',
  CREDITS_EXHAUSTED: 'credits_exhausted',
};

// Action values for upsell_prompt_clicked.
export const UPSELL_ACTIONS = {
  UPGRADE: 'upgrade',
  TOP_UP: 'top_up',
  FREE_UP_SPACE: 'free_up_space',
  VIEW_PLANS: 'view_plans',
  MANAGE_BILLING: 'manage_billing',
};

// Catalog order is cheapest-first, which is what "the tier that unlocks it"
// means: the least the user has to pay to clear the wall they just hit.
const selfServeTiers = () => PRICING_TIERS.filter((tier) => tier.planId);

/**
 * The cheapest self-serve tier that lifts this specific restriction, or null
 * when none does.
 *
 * @param promptId one of UPSELL_PROMPTS
 * @param context.poolBytes the pool the account is actually metered against
 *   (caps.poolTotalBytes / poolBytesFromGb(entitlements.storageGb)) — NOT a
 *   tier id, because a founding member's grandfather floor can exceed the
 *   catalog and comparing catalog-to-catalog would pitch them a smaller pool.
 * @param context.monthlyCredits the account's current monthly allotment.
 */
export function resolveUnlockTier(promptId, context = {}) {
  const tiers = selfServeTiers();
  switch (promptId) {
    case UPSELL_PROMPTS.WATERMARK_LOCKED:
      return tiers.find((tier) => tier.watermarkFree) || null;
    case UPSELL_PROMPTS.STORAGE_POOL_FULL: {
      const { poolBytes } = context;
      // Unknown pool → withhold the pitch rather than sell a fix that may not
      // be one (same posture as hasLargerStoragePoolThan).
      if (!Number.isFinite(poolBytes)) return null;
      return tiers.find((tier) => {
        const tierBytes = poolBytesFromGb(tier.storageGb);
        return Number.isFinite(tierBytes) && tierBytes > poolBytes;
      }) || null;
    }
    case UPSELL_PROMPTS.CREDITS_EXHAUSTED: {
      const current = Number(context.monthlyCredits) || 0;
      return tiers.find((tier) => (Number(tier.monthlyCredits) || 0) > current) || null;
    }
    default:
      return null;
  }
}

const plural = (n, word) => `${n} ${n === 1 ? word : `${word}s`}`;

/**
 * Headline + body for a prompt. `tier` is the resolveUnlockTier result and may
 * be null, in which case the copy makes no upgrade promise.
 */
export function buildUpsellCopy(promptId, tier, context = {}) {
  const tierName = tier ? tier.name : null;
  switch (promptId) {
    case UPSELL_PROMPTS.WATERMARK_LOCKED:
      return {
        // "Remove watermark" — the exact control label they just clicked.
        title: tierName
          ? `${OPERATIONS.watermark.controlLabel} with ${tierName}`
          : OPERATIONS.watermark.controlLabel,
        body: tierName
          ? `Free renders carry a small Tweedia Studio wordmark in the corner. ${tierName} removes it — every export comes out clean, up to 4K.`
          : 'Free renders carry a small Tweedia Studio wordmark in the corner.',
      };
    case UPSELL_PROMPTS.STORAGE_POOL_FULL:
      return {
        title: 'Your storage pool is full',
        body: tierName
          ? `Free up space by removing photos or projects you no longer need, or move to ${tierName} for a bigger pool.`
          : 'Free up space by removing photos or projects you no longer need.',
      };
    case UPSELL_PROMPTS.CREDITS_EXHAUSTED: {
      const shortBy = Number(context.shortBy);
      const haveShortfall = Number.isFinite(shortBy) && shortBy > 0;
      return {
        title: haveShortfall
          ? `You're ${plural(shortBy, 'credit')} short`
          : 'You’re out of credits',
        body: tierName
          ? `Top up to render this video now, or move to ${tierName} for ${tier.monthlyCredits} credits every month.`
          : 'Top up to render this video now.',
      };
    }
    default:
      return { title: 'Upgrade your plan', body: '' };
  }
}
